import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { DPE } from '../@types/DPE';
import type { Owner } from '../@types/Owner';
import { showToast } from '../components/common/Notification/';
import { fetchDpesData } from '../components/Prospecting/OwnerCommunication/utils/OwnerCommunicationService';


interface FilteredMonth {
  date: string;
  type?: 'reception' | 'etablissement';
}


interface DpesState {
  data: DPE[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  expandedRows: number;
  cloturerRows: number;
  showPhone: number[];
  monthFiltered?: FilteredMonth;
  reloadOwners: boolean;
}

interface FetchDpesArgs {
  companyId: number;
  jwt: string;
  monthFiltered?: FilteredMonth;
}

export const fetchDpes = createAsyncThunk<any[], FetchDpesArgs>(
  'dpeOwner/fetchDpes',
  async({ companyId, jwt, monthFiltered }) => {
    const response = await fetchDpesData(companyId, jwt, monthFiltered);
    return response;
  },
);

const initialState: DpesState = {
  data: [],
  status: 'idle',
  error: null,
  expandedRows: null,
  cloturerRows: null,
  showPhone: [],
  monthFiltered: null,
  reloadOwners: false,
};

const dpeOwnerSlice = createSlice({
  name: 'dpeOwner',
  initialState,
  reducers: {
    updateOwnerOfDpe(state, action: PayloadAction<[dpeId: number, updatedOwner: Owner]>) {
      const [dpeId, updatedOwner] = action.payload;
      const dpeToUpdate = state.data.find(dpe => dpe.id === dpeId);
      if (dpeToUpdate) {
        const ownerIndex = dpeToUpdate.owners.findIndex(owner => owner.id === updatedOwner.id);
        if (ownerIndex >= 0) {
          dpeToUpdate.owners[ownerIndex] = { ...dpeToUpdate.owners[ownerIndex], ...updatedOwner };
        }
      }
    },
    setExpandedRows(state, action: PayloadAction<number>) {
      state.expandedRows = action.payload;
    },
    setCloturerRows(state, action: PayloadAction<number>) {
      state.cloturerRows = action.payload;
    },
    setShowPhone(state, action: PayloadAction<number[]>) {
      state.showPhone = action.payload;
    },
    setMonthFiltered(state, action: PayloadAction<string>) {
      state.monthFiltered = { date: action.payload, type: state.monthFiltered?.type || 'reception' };
    },
    setTypeFiltered(state, action: PayloadAction<string>) {
      if (action.payload == 'reception' || action.payload == 'etablissement') {
        state.monthFiltered = { date: state.monthFiltered?.date, type: action.payload };
      } else {
        throw new Error('Invalid type');
      }
    },
    setReloadOwners(state, action: PayloadAction<boolean>) {
      state.reloadOwners = action.payload;
    },
    setContact(state, action: PayloadAction<any>) {
      // use action.payload.ownerId to add a new contact to the owner at the begginning of the contacts array
      state.data = state.data.map((dpe) => ({
        ...dpe,
        owners: dpe.owners.map((owner) => {
          if (owner.id !== action.payload.ownerId) {
            return owner;
          }
          return {
            ...owner,
            contacts: [
              {
                id: action.payload.contact.id,
                type: action.payload.contact.type,
                createdAt: action.payload.contact.createdAt,
                updatedAt: action.payload.contact.updatedAt,
              },
              ...owner.contacts,
            ],
          };
        }),
      }));
    },
    setInteraction(state, action: PayloadAction<{ ownerId: number }>) {
      // use action.payload.ownerId to add a new interaction to remove the owner from the list of owners to treat
      state.data = state.data.map((dpe) => ({
        ...dpe,
        owners: dpe.owners.filter((owner) => owner.id !== action.payload.ownerId),
      }));
      // check if the dpe is empty and remove it from the list if no more owners
      state.data = state.data.filter((dpe) => dpe.owners.length > 0);
    },
    updateOwner(state, action: PayloadAction<{ ownerId: number, updatedOwner: Owner }>) {
      const { ownerId, updatedOwner } = action.payload;
      state.data = state.data.map((dpe) => ({
        ...dpe,
        owners: dpe.owners.map((owner) =>
          owner.id === ownerId ? updatedOwner : owner,
        ),
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDpes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDpes.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        // reset expandedRows and cloturerRows when fetching new data
        state.expandedRows = null;
        state.cloturerRows = null;
      })
      .addCase(fetchDpes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
        showToast('error', 'Une erreur est survenue lors de la récupération des données.');
      });
  },
});

export const { 
  updateOwnerOfDpe,
  setExpandedRows,
  setCloturerRows,
  setShowPhone,
  setContact,
  setInteraction,
  setMonthFiltered,
  setTypeFiltered,
  setReloadOwners,
  updateOwner } = dpeOwnerSlice.actions;

export default dpeOwnerSlice.reducer;
